import { useMemo } from 'react';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { INVALID, UNKNOWN, VALID } from 'customer-data-properties/validation/PropertyValidationStatus';
import { NOT_SPECIFIED } from 'customer-data-objects/property/AccessLevel';
import emptyFunction from 'react-utils/emptyFunction';
import { isReadOnly } from 'customer-data-objects/property/PropertyIdentifier';
import PropertyCellDisplayContent from './PropertyCellDisplayContent';
import { shouldRightAlignProperty } from '../../utils/shouldRightAlignProperty';
import TruncateDiv from '../TruncateDiv';
import { useManagePropertyEditMode } from '../editable/hooks/useManagePropertyEditMode';
import { EditableCell, EditableCellSpan, EditCellContentAndValidationAlertWrapper } from './StyledEditCellComponents';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import InlineEditCell from '../editable/InlineEditCell';
import { isReadOnlySubscriptionProperty } from 'customer-data-objects/subscription/isReadOnlySubscriptionProperty';
import { isReadOnlyProductProperty } from 'customer-data-objects/product/isReadOnlyProductProperty';
import { EditCell, EditCellSpan } from '../editable/StyledInlineEditCellComponents';
import { useAuth } from '../../auth/context/AuthContext';
import { gate } from 'hub-http/gates';
import withGateOverride from '../../utils/withGateOverride';
import { useTrellisTheme } from '../../utils/useTrellisTheme';

// This allows us to reuse whatever type we declared in PropertyCellDisplayContentProps.
// Alternatively we could have just used Property directly, but if its ever updated, it would need to be updated twice
//By inferring a type here, it allows us to keep originalValue in sync with value with only one change
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultDoPreventEdit = () => false;

/**
 * doPreventEdit is a function that takes in a propertyDefinition and returns true or false.
 * If false is returned, it guarentees this cell will not be editable regardless of other checks such as FLP, or canEditObject
 *
 *
 * originalValue is used for "has this value changed" comparision. Primiarily this is important for firing off validation requests.
 * see https://hubspot.slack.com/archives/C030PAFHWNM/p1659708158423089?thread_ts=1659635271.279239&cid=C030PAFHWNM
 **/
const PropertyEditCell = ({
  objectTypeId,
  objectId,
  propertyDefinition,
  canEditObject,
  getSecondaryPropertyValue,
  onPropertyUpdatesSucceeded = emptyFunction,
  onPropertyUpdatesFailed = emptyFunction,
  onPropertyUpdates = emptyFunction,
  onValidationChange = emptyFunction,
  requiredCellProps,
  value,
  originalValue,
  doPreventEdit = defaultDoPreventEdit,
  validationResult,
  enableConditionalPropertiesPanel = false,
  enableConditionalPropertyOptions = false,
  enableAutosave = false,
  onAutosave,
  enableEditOnDoubleClick = false,
  CellContentComponent = PropertyCellDisplayContent,
  tdRef,
  tdSlot = null,
  as: WrapperElement = 'td'
}) => {
  const {
    inEditMode,
    handleButtonCellRef,
    handleKeyDown,
    enableEditMode,
    disableEditMode,
    handleCompositionEnd
  } = useManagePropertyEditMode();
  const {
    hasGate
  } = useAuth();
  const buttonCellRefCallback = useMemo(() => ref => {
    handleButtonCellRef(ref);
    if (tdRef) {
      tdRef.current = ref;
    }
  }, [handleButtonCellRef, tdRef]);
  const validationStateIsDetermined = validationResult && [VALID, INVALID, UNKNOWN].includes(validationResult.validationStatus);
  const userHasChangedValue = value !== originalValue;
  const accessLevel = useAccessLevel({
    objectType: objectTypeId,
    propertyName: propertyDefinition.name
  });
  const canEditValue = useMemo(() => {
    const preventEdit = doPreventEdit({
      propertyDefinition
    });
    const readOnly = isReadOnly(propertyDefinition);
    const canFLPEditProperty = !propertyDefinition.readOnlyValue && accessLevel === NOT_SPECIFIED;
    return canFLPEditProperty && canEditObject && !preventEdit && !readOnly && !isReadOnlySubscriptionProperty({
      objectTypeId,
      propertyName: propertyDefinition.name,
      hubspotDefined: propertyDefinition.hubspotDefined,
      invoiceCreation: getSecondaryPropertyValue('hs_invoice_creation'),
      collectionProcess: getSecondaryPropertyValue('hs_collection_process')
    }) && !isReadOnlyProductProperty(objectTypeId, propertyDefinition, getSecondaryPropertyValue);
  }, [accessLevel, canEditObject, doPreventEdit, propertyDefinition, objectTypeId, getSecondaryPropertyValue]);
  const errorMessage = validationResult && validationResult.message;
  const doesErrorExist = errorMessage !== undefined;
  const waitingForValidation = userHasChangedValue && !validationStateIsDetermined;
  const showInlineEditCell = inEditMode || waitingForValidation;
  const hasAutosaveGate = withGateOverride('CRM:Properties:Autosave', hasGate(gate('CRM:Properties:Autosave')));
  const theme = useTrellisTheme();
  if (showInlineEditCell) {
    const EditCellWrapperElement = WrapperElement === 'span' ? EditCellSpan : EditCell;
    return /*#__PURE__*/_jsx(EditCellWrapperElement, Object.assign({
      "data-editing": "true",
      $theme: theme
    }, requiredCellProps, {
      children: /*#__PURE__*/_jsx(InlineEditCell, {
        objectId: objectId,
        objectTypeId: objectTypeId,
        propertyDefinition: propertyDefinition,
        getSecondaryPropertyValue: getSecondaryPropertyValue,
        disableEditMode: disableEditMode,
        handleKeyDown: handleKeyDown,
        onPropertyUpdates: onPropertyUpdates,
        onValidationChange: onValidationChange,
        validationResult: validationResult,
        value: value,
        originalValue: originalValue,
        canEditValue: canEditValue,
        enableConditionalPropertiesPanel: enableConditionalPropertiesPanel,
        enableConditionalPropertyOptions: enableConditionalPropertyOptions,
        enableAutosave: hasAutosaveGate ? enableAutosave : false,
        onAutosave: hasAutosaveGate ? onAutosave : undefined,
        handleCompositionEnd: handleCompositionEnd
      })
    }));
  }
  const rightAligned = shouldRightAlignProperty(propertyDefinition);
  const maybeValidationAlert = doesErrorExist && /*#__PURE__*/_jsx(UITooltip, {
    title: errorMessage,
    children: /*#__PURE__*/_jsx(UIIcon, {
      "data-test-id": "property-edit-cell-validation-error-icon",
      name: "warning",
      color: "red"
    })
  });

  /*
  NOTE: EditCellContentAndValidationAlertWrapper is 'display: flex' with 'justify-content: space-between'
  by rendering a second element before of after PropertyCellDisplayContent even if there isnt an error, we force
  css to push PropertyCellDisplayContent to the far right or far left of the container giving us right or left alignment
  even if there isnt an error message. If this breaks, it will look like this
  https://hubspot.slack.com/archives/CEECRATK2/p1660744680787419?thread_ts=1660743777.338939&cid=CEECRATK2
  this wrapping span is the "second element" described above - its rendered even if there isnt an error
  */
  const validationAlertOrEmptySpacer = /*#__PURE__*/_jsx("span", {
    children: maybeValidationAlert
  });
  const displayContent = /*#__PURE__*/_jsxs(EditCellContentAndValidationAlertWrapper, {
    children: [rightAligned && validationAlertOrEmptySpacer, /*#__PURE__*/_jsx(TruncateDiv, {
      children: /*#__PURE__*/_jsx(CellContentComponent, {
        value: value,
        objectId: objectId,
        objectTypeId: objectTypeId,
        propertyDefinition: propertyDefinition
        // Disables click-to-assign
        ,
        canEditObject: false,
        onClick: enableEditMode,
        getSecondaryPropertyValue: getSecondaryPropertyValue,
        onPropertyUpdatesSucceeded: onPropertyUpdatesSucceeded,
        onPropertyUpdatesFailed: onPropertyUpdatesFailed
      })
    }), !rightAligned && validationAlertOrEmptySpacer]
  });
  if (canEditValue) {
    const EditableCellWrapperElement = WrapperElement === 'span' ? EditableCellSpan : EditableCell;
    return /*#__PURE__*/_jsxs(EditableCellWrapperElement, Object.assign({}, requiredCellProps, {
      tabIndex: 0,
      role: "button",
      ref: buttonCellRefCallback,
      onKeyUp: handleKeyDown,
      onClick: enableEditOnDoubleClick ? undefined : enableEditMode,
      onDoubleClick: enableEditOnDoubleClick ? enableEditMode : undefined,
      "aria-invalid": doesErrorExist,
      "aria-pressed": userHasChangedValue,
      "data-test-id": "framework-data-table-editable-cell",
      children: [displayContent, tdSlot]
    }));
  }
  return /*#__PURE__*/_jsxs(WrapperElement, Object.assign({}, requiredCellProps, {
    children: [displayContent, tdSlot]
  }));
};
export default PropertyEditCell;